import type { VoDeltaLine } from "@shared/variationOrderDiff";
import { resolveVoItemDisplayChangeReason } from "@shared/variationOrderChangeReason";
import { computeVoPresentationTotals } from "@shared/variationOrderTypes";
import {
  mapVoDocumentLineCommercialAmounts,
  type VoPaymentDetailsSnapshot,
} from "@shared/variationOrderPaymentDetails";
import type { VoSystemOverviewChange } from "@shared/variationOrderSystemOverview";
import { groupVoReplacementPairs } from "@shared/dtoolsProductReplace";
import {
  renderProductSubtitle,
  renderVoAbsoluteMoney,
  renderVoMoneyDelta,
  renderVoRoomHeader,
  renderVoSystemHeader,
} from "@shared/rendering";
import { ArrowDown } from "lucide-react";
import VoPriceAdjustmentSection from "@/components/proposals/VoPriceAdjustmentSection";

function money(n: number) {
  return renderVoMoneyDelta(n);
}

function moneyAbs(n: number) {
  return renderVoAbsoluteMoney(n);
}

function moneyRemoved(n: number) {
  return `-${renderVoAbsoluteMoney(n)}`;
}

function moneyAdded(n: number) {
  return `+${renderVoAbsoluteMoney(n)}`;
}

function VoItemReasonBlock({ reason }: { reason: string }) {
  return (
    <div className="rounded-md border border-[#1A2744]/10 bg-[#F7F5F0] px-3 py-2" data-testid="vo-line-reason">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">Reason</p>
      <p className="mt-0.5 text-sm text-[#1A2744]">{reason}</p>
    </div>
  );
}

function VoItemTitleBlock({
  line,
}: {
  line: Pick<VoDeltaLine, "name" | "brand" | "model" | "description">;
}) {
  const subtitle = renderProductSubtitle(line);
  return (
    <>
      <dd className="mt-0.5 font-medium text-[#1A2744]">{line.name}</dd>
      {subtitle ? (
        <p className="mt-0.5 text-xs text-muted-foreground" data-testid="vo-item-subtitle">
          {subtitle}
        </p>
      ) : null}
    </>
  );
}

function VoLineCard({
  line,
  quantity,
  amount,
  amountClassName,
  voChangeReason,
}: {
  line: VoDeltaLine;
  quantity: number | null;
  amount: number;
  amountClassName?: string;
  voChangeReason: string | null;
}) {
  const displayReason = resolveVoItemDisplayChangeReason({
    lineChangeReason: line.changeReason,
    voChangeReason,
  });
  return (
    <li className="space-y-3 px-4 py-4 text-sm" data-testid={`vo-line-${line.changeType}-${line.stableLineKey}`}>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">System</dt>
          <dd className="mt-0.5 font-medium text-[#1A2744]">{renderVoSystemHeader(line)}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">Room</dt>
          <dd className="mt-0.5 font-medium text-[#1A2744]">{renderVoRoomHeader(line)}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">Item</dt>
          <VoItemTitleBlock line={line} />
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">Quantity</dt>
          <dd className="mt-0.5 tabular-nums text-[#1A2744]">
            {line.changeType === "quantity_changed"
              ? `${line.quantityBefore ?? "—"} → ${line.quantityAfter ?? "—"}`
              : quantity ?? "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">Amount</dt>
          <dd
            className={`mt-0.5 tabular-nums font-semibold ${amountClassName ?? ""}`}
            data-testid={`vo-line-amount-${line.stableLineKey}`}
          >
            {money(amount)}
          </dd>
        </div>
      </dl>
      {displayReason ? <VoItemReasonBlock reason={displayReason} /> : null}
    </li>
  );
}

function VoReplacementCard({
  removed,
  added,
  reason,
  commercialDelta,
  voChangeReason,
}: {
  removed: VoDeltaLine;
  added: VoDeltaLine;
  reason: string | null;
  commercialDelta: number;
  voChangeReason: string | null;
}) {
  const displayReason = resolveVoItemDisplayChangeReason({
    lineChangeReason: reason,
    voChangeReason,
  });
  const removedSubtitle = renderProductSubtitle(removed);
  const addedSubtitle = renderProductSubtitle(added);
  return (
    <li
      className="space-y-3 px-4 py-4 text-sm"
      data-testid={`vo-line-replaced-${removed.stableLineKey}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#C8A96A]">Replaced</p>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
        <div className="rounded-md border border-[#1A2744]/10 bg-white p-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">
            Old product
          </p>
          <p className="mt-1 font-medium text-[#1A2744]">{removed.name}</p>
          {removedSubtitle ? (
            <p className="text-xs text-muted-foreground" data-testid="vo-item-subtitle">
              {removedSubtitle}
            </p>
          ) : null}
        </div>
        <div className="flex justify-center text-muted-foreground">
          <ArrowDown className="h-4 w-4 sm:rotate-[-90deg]" aria-hidden />
        </div>
        <div className="rounded-md border border-[#1A2744]/10 bg-white p-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">
            New product
          </p>
          <p className="mt-1 font-medium text-[#1A2744]">{added.name}</p>
          {addedSubtitle ? (
            <p className="text-xs text-muted-foreground" data-testid="vo-item-subtitle">
              {addedSubtitle}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {renderVoSystemHeader(added)} · {renderVoRoomHeader(added)}
        </p>
        <p
          className="tabular-nums text-sm font-semibold text-[#1A2744]"
          data-testid={`vo-replacement-amount-${removed.stableLineKey}`}
        >
          Commercial difference {money(commercialDelta)}
        </p>
      </div>
      {displayReason ? (
        <div
          className="rounded-md border border-[#1A2744]/10 bg-[#F7F5F0] px-3 py-2"
          data-testid="vo-replacement-reason"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">Reason</p>
          <p className="mt-0.5 text-sm text-[#1A2744]">{displayReason}</p>
        </div>
      ) : null}
    </li>
  );
}

export type VariationOrderDocumentProps = {
  voNumber: string;
  proposalRef: string | null;
  customerName: string | null;
  dateLabel: string | null;
  status: string;
  lines: VoDeltaLine[];
  originalContractValue: number;
  variationAmount: number;
  revisedContractValue: number;
  /** Overall customer-facing VO reason (optional). */
  changeReason?: string | null;
  /**
   * Commercial Payment Details snapshot — sole financial presentation authority
   * for draft preview, Link A, Link B, approved VO, and print/PDF.
   */
  paymentDetails?: VoPaymentDetailsSnapshot | null;
  /**
   * Only historical pre-commercial VOs may render the legacy retail summary.
   * Commercial-era VOs must not silently fall back when the snapshot is missing.
   */
  allowsLegacySummary?: boolean;
  /** Href to the master proposal (staff Link A or customer Link B). */
  fullProposalHref?: string | null;
  /** Updated System Overview amendments (omit section when empty). */
  systemOverviewChanges?: VoSystemOverviewChange[] | null;
  children?: React.ReactNode;
};

export default function VariationOrderDocument({
  voNumber,
  proposalRef,
  customerName,
  dateLabel,
  status,
  lines,
  originalContractValue,
  changeReason,
  paymentDetails,
  allowsLegacySummary = false,
  fullProposalHref,
  systemOverviewChanges = null,
  children,
}: VariationOrderDocumentProps) {
  const { replacements, unpaired } = groupVoReplacementPairs(lines);
  const removed = unpaired.filter((l) => l.changeType === "removed");
  const added = unpaired.filter((l) => l.changeType === "added");
  const qty = unpaired.filter((l) => l.changeType === "quantity_changed");
  const overviewUpdates = (systemOverviewChanges ?? []).filter(
    (c) => c.changeType === "overview_updated" && String(c.revisedOverview ?? "").trim(),
  );
  const headerReason = String(changeReason ?? "").trim() || null;
  const commercialByKey = paymentDetails
    ? mapVoDocumentLineCommercialAmounts(lines, paymentDetails)
    : null;
  const lineAmount = (line: VoDeltaLine) =>
    commercialByKey?.[line.stableLineKey] ?? Number(line.lineTotalDelta ?? 0);
  // Legacy fallback only when explicitly allowed for historical pre-commercial VOs.
  const legacyTotals =
    !paymentDetails && allowsLegacySummary
      ? computeVoPresentationTotals({ originalContractValue, lines })
      : null;

  if (!paymentDetails && !allowsLegacySummary && process.env.NODE_ENV === "development") {
    console.error(
      "[VariationOrderDocument] commercial Payment Details missing; refusing legacy retail summary",
      { voNumber, status, lineCount: lines.length },
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-8" data-testid="variation-order-document">
      <header className="space-y-2 border-b border-[#1A2744]/15 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#C8A96A]">Variation Order</p>
        <h1 className="text-2xl font-semibold text-[#1A2744]">{voNumber}</h1>
        <dl className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          <div>
            <dt className="inline font-medium text-[#1A2744]">Reference proposal: </dt>
            <dd className="inline">{proposalRef || "—"}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-[#1A2744]">Customer: </dt>
            <dd className="inline">{customerName || "—"}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-[#1A2744]">Date: </dt>
            <dd className="inline">{dateLabel || "—"}</dd>
          </div>
          <div>
            <dt className="inline font-medium text-[#1A2744]">Status: </dt>
            <dd className="inline capitalize">{status}</dd>
          </div>
        </dl>
        {fullProposalHref ? (
          <p className="pt-2">
            <a
              href={fullProposalHref}
              className="text-sm font-medium text-[#1A2744] underline-offset-2 hover:underline"
              data-testid="vo-view-full-proposal"
            >
              View Full Proposal
            </a>
          </p>
        ) : null}
      </header>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">Summary</h2>
        <p className="text-sm text-muted-foreground">
          This Variation Order is an amendment explaining what changed. The proposal is the living contract — open it
          for the latest agreed scope, rooms, and pricing.
        </p>
      </section>

      {headerReason ? (
        <section
          className="space-y-2 rounded-lg border border-[#1A2744]/12 bg-[#F7F5F0] px-4 py-3"
          data-testid="vo-document-change-reason"
        >
          <h2 className="text-sm font-semibold text-[#1A2744]">Change reason</h2>
          <p className="text-sm whitespace-pre-wrap text-[#1A2744]">{headerReason}</p>
        </section>
      ) : null}

      {replacements.length > 0 ? (
        <section className="space-y-3" data-testid="vo-document-replacements">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">Replaced Items</h2>
          <ul className="divide-y rounded-lg border border-[#1A2744]/12 bg-white">
            {replacements.map((pair) => (
              <VoReplacementCard
                key={`${pair.removed.stableLineKey}:${pair.added.stableLineKey}`}
                removed={pair.removed}
                added={pair.added}
                reason={pair.reason}
                commercialDelta={lineAmount(pair.added) + lineAmount(pair.removed)}
                voChangeReason={headerReason}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {removed.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">Items Removed</h2>
          <ul className="divide-y rounded-lg border border-[#1A2744]/12 bg-white">
            {removed.map((l) => (
              <VoLineCard
                key={`r-${l.stableLineKey}`}
                line={l}
                quantity={l.quantityBefore}
                amount={lineAmount(l)}
                amountClassName="text-destructive"
                voChangeReason={headerReason}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {added.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">Items Added</h2>
          <ul className="divide-y rounded-lg border border-[#1A2744]/12 bg-white">
            {added.map((l) => (
              <VoLineCard
                key={`a-${l.stableLineKey}`}
                line={l}
                quantity={l.quantityAfter}
                amount={lineAmount(l)}
                voChangeReason={headerReason}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {qty.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">Quantity Changes</h2>
          <ul className="divide-y rounded-lg border border-[#1A2744]/12 bg-white">
            {qty.map((l) => (
              <VoLineCard
                key={`q-${l.stableLineKey}`}
                line={l}
                quantity={l.quantityAfter}
                amount={lineAmount(l)}
                voChangeReason={headerReason}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {overviewUpdates.length > 0 ? (
        <section className="space-y-3" data-testid="vo-document-system-overview-updates">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">
            System Overview Updates
          </h2>
          <ul className="divide-y rounded-lg border border-[#1A2744]/12 bg-white">
            {overviewUpdates.map((change) => (
              <li
                key={change.systemKey}
                className="space-y-3 px-4 py-4 text-sm"
                data-testid={`vo-overview-update-${change.systemKey}`}
              >
                <p className="font-medium text-[#1A2744]">{change.systemName}</p>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
                  <div className="rounded-md border border-[#1A2744]/10 bg-white p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#1A2744]/55">
                      Previous
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-[#1A2744]">
                      {change.originalOverview || "—"}
                    </p>
                  </div>
                  <div className="flex justify-center pt-6 text-muted-foreground">
                    <ArrowDown className="h-4 w-4 sm:rotate-[-90deg]" aria-hidden />
                  </div>
                  <div className="rounded-md border border-[#C8A96A]/40 bg-[#F7F5F0] p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#C8A96A]">
                      Updated
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-[#1A2744]">{change.revisedOverview}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {lines.length === 0 && overviewUpdates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No equipment changes recorded yet.</p>
      ) : null}

      {paymentDetails ? (
        <VoPriceAdjustmentSection paymentDetails={paymentDetails} voNumber={voNumber} />
      ) : legacyTotals ? (
        <section
          className="space-y-3 rounded-xl border border-[#1A2744]/12 bg-[#FAFAF7] p-4"
          data-testid="vo-order-summary"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#1A2744]">
            Variation Order Summary
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt>Original Contract Value</dt>
              <dd className="tabular-nums font-medium">{moneyAbs(legacyTotals.originalContractValue)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>Removed Items</dt>
              <dd className="tabular-nums font-medium text-destructive">
                {moneyRemoved(legacyTotals.removedItemsTotal)}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>Added Items</dt>
              <dd className="tabular-nums font-medium">{moneyAdded(legacyTotals.addedItemsTotal)}</dd>
            </div>
            <div className="flex justify-between gap-3 border-t border-[#1A2744]/15 pt-3 text-base">
              <dt className="font-semibold text-[#1A2744]">Revised Contract Total</dt>
              <dd
                className="tabular-nums font-semibold text-[#1A2744]"
                data-testid="vo-revised-contract-total"
              >
                {moneyAbs(legacyTotals.revisedContractTotal)}
              </dd>
            </div>
          </dl>
        </section>
      ) : (
        <section
          className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4"
          data-testid="vo-commercial-missing"
        >
          <h2 className="text-sm font-semibold text-amber-950">Commercial pricing unavailable</h2>
          <p className="text-sm text-amber-950">
            Payment Details for this Variation Order could not be loaded. Save the VO draft again, then
            reopen Preview. The legacy retail summary is intentionally not shown for commercial VOs.
          </p>
        </section>
      )}

      {children}
    </div>
  );
}
